import { useState } from "react";
import axios from "axios";
import { Envelope, ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { setUserId } from "@/lib/userId";

/**
 * EmailLinkPanel
 * Two-step OTP flow used in both Onboarding and Settings.
 * Props:
 *  - api: base API url
 *  - onLinked(profile): called after successful verification
 *  - compact: if true, hides the heading & description (for embedded use)
 *  - submitLabel: label on the send button (defaults to "Send Code")
 */
export default function EmailLinkPanel({ api, onLinked, compact = false, submitLabel = "Send Code" }) {
  const [phase, setPhase] = useState("input"); // input | code | success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const sendOtp = async () => {
    setError("");
    setBusy(true);
    try {
      await axios.post(`${api}/email/send-otp`, { email: email.trim() });
      setPhase("code");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Couldn't send the code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await axios.post(`${api}/email/verify`, { email: email.trim(), code: code.trim() });
      // Swap to the email-keyed user_id so data follows the user across devices
      if (res.data?.new_user_id) {
        setUserId(res.data.new_user_id);
      }
      setPhase("success");
      onLinked?.(res.data?.profile);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-link-panel">
      {!compact && (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center border border-[#27272A] text-[#E05A1D] shrink-0">
            <Envelope size={20} weight="bold" />
          </div>
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-white">Link your email</p>
            <p className="text-xs text-[#71717A] mt-0.5">Optional — for weekly recaps & syncing across devices.</p>
          </div>
        </div>
      )}

      {phase === "input" && (
        <div className="space-y-3" data-testid="email-input-phase">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && validEmail && !busy && sendOtp()}
            placeholder="you@example.com"
            className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-4 py-3 text-sm focus:border-[#E05A1D] outline-none transition-all"
            autoFocus
            data-testid="email-link-input"
          />
          <button
            onClick={sendOtp}
            disabled={!validEmail || busy}
            className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-30 disabled:hover:bg-[#E05A1D] text-white font-bold uppercase tracking-wide py-3 text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            data-testid="send-otp-btn"
          >
            {busy ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {submitLabel}
                <ArrowRight size={16} weight="bold" />
              </>
            )}
          </button>
        </div>
      )}

      {phase === "code" && (
        <div className="space-y-3" data-testid="otp-code-phase">
          <p className="text-xs text-[#A1A1AA]">
            We sent a 6-digit code to <span className="text-white font-semibold">{email}</span>. It expires in 10 minutes.
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && !busy && verifyOtp()}
            placeholder="••••••"
            className="w-full bg-[#0F0F0F] border border-[#27272A] text-white text-2xl font-bold tracking-[0.5em] text-center py-4 focus:border-[#E05A1D] outline-none transition-all"
            autoFocus
            data-testid="otp-input"
          />
          <button
            onClick={verifyOtp}
            disabled={code.length !== 6 || busy}
            className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-30 disabled:hover:bg-[#E05A1D] text-white font-bold uppercase tracking-wide py-3 text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            data-testid="verify-otp-btn"
          >
            {busy ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Verify & Link
                <CheckCircle size={16} weight="bold" />
              </>
            )}
          </button>
          <button
            onClick={() => {
              setCode("");
              setError("");
              setPhase("input");
            }}
            className="w-full text-xs text-[#71717A] hover:text-white font-semibold uppercase tracking-wider transition-colors"
            data-testid="otp-change-email-btn"
          >
            Use a different email
          </button>
        </div>
      )}

      {phase === "success" && (
        <div className="flex items-center gap-3 border border-[#22c55e]/30 bg-[#22c55e]/10 p-4" data-testid="email-link-success">
          <CheckCircle size={22} weight="fill" className="text-[#22c55e] shrink-0" />
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-white">Email linked</p>
            <p className="text-xs text-[#A1A1AA] mt-0.5">{email}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 border border-red-500/30 bg-red-500/10 p-3" data-testid="email-link-error">
          <Warning size={16} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-300 leading-relaxed">{error}</p>
        </div>
      )}
    </div>
  );
}
